import { create } from 'zustand';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  /** One thing to do about it, such as opening Needs attention. */
  action?: ToastAction;
}

interface ToastState {
  toasts: Toast[];
  dismiss: (id: number) => void;
}

/** How long a toast stays. Errors stay until closed: they may need reading twice. */
const LIFETIME_MS = 5000;
const MAX_SHOWN = 4;

let nextId = 1;

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

function push(tone: ToastTone, message: string, action?: ToastAction): void {
  const id = nextId++;
  useToasts.setState((s) => ({
    toasts: [...s.toasts, { id, tone, message, ...(action ? { action } : {}) }].slice(-MAX_SHOWN),
  }));
  if (tone !== 'error') setTimeout(() => useToasts.getState().dismiss(id), LIFETIME_MS);
}

/**
 * A short notice in the corner: "Company settings saved", "Connection is
 * back". For news about something that already happened. A form's own
 * validation errors stay next to the form, where the fix is made.
 */
export const toast = {
  success: (message: string, action?: ToastAction) => push('success', message, action),
  error: (message: string, action?: ToastAction) => push('error', message, action),
  info: (message: string, action?: ToastAction) => push('info', message, action),
};
